import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Button } from '../components/ui/Button';
import {
  CompassIcon,
  MapPinIcon,
  GroupIcon,
} from '../components/icons';

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 }
    );

    const children = el.querySelectorAll('[data-reveal]');
    children.forEach((child) => observer.observe(child));

    return () => observer.disconnect();
  }, []);

  return ref;
}

export function LandingPage() {
  const revealRef = useScrollReveal();

  return (
    <div ref={revealRef} className="min-h-screen bg-[#F0F2F8]">
      <SEO
        title="Tractk"
        description="Tractk — Real Connections. Differently. True Relationships, Authentic Connections, Through Knowing."
        ogTitle="Tractk — Real Connections. Differently."
      />
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-primary-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#6B8FD4] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">T</span>
            </div>
            <span className="font-semibold text-xl text-[#1A2B3C]">Tractk</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" size="sm">Sign in</Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">Get started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-4 sm:px-6 lg:px-8 bg-[#f4f7fc]" style={{ paddingTop: 64, paddingBottom: 72 }}>
        <div className="max-w-6xl mx-auto text-center">
          {/* Wordmark — image asset */}
          <img
            data-reveal
            src="/tractk-wordmark.png"
            alt="TRACTK"
            className="block h-auto transition-all duration-700 opacity-0 translate-y-8"
            style={{ width: 'min(810px, 78vw)', margin: '0 auto 26px auto' }}
          />

          {/* Acronym line */}
          <p
            data-reveal
            className="text-[#1A2B3C]/40 font-light transition-all duration-700 delay-100 opacity-0 translate-y-8"
            style={{ fontSize: 15, letterSpacing: '0.18em', marginBottom: 32 }}
          >
            <span className="text-[#6B8FD4] font-normal">T</span>rue{' '}
            <span className="text-[#1A2B3C]/15 mx-1">|</span>{' '}
            <span className="text-[#6B8FD4] font-normal">R</span>elationships{' '}
            <span className="text-[#1A2B3C]/15 mx-1">|</span>{' '}
            <span className="text-[#6B8FD4] font-normal">A</span>uthentic{' '}
            <span className="text-[#1A2B3C]/15 mx-1">|</span>{' '}
            <span className="text-[#6B8FD4] font-normal">C</span>onnections{' '}
            <span className="text-[#1A2B3C]/15 mx-1">|</span>{' '}
            <span className="text-[#6B8FD4] font-normal">T</span>hrough{' '}
            <span className="text-[#1A2B3C]/15 mx-1">|</span>{' '}
            <span className="text-[#6B8FD4] font-normal">K</span>nowing
          </p>

          {/* Tagline */}
          <p
            data-reveal
            className="uppercase text-[#1A2B3C]/50 font-light transition-all duration-700 delay-150 opacity-0 translate-y-8"
            style={{ fontSize: 18, letterSpacing: '0.35em', marginBottom: 28 }}
          >
            Real Connections. Differently.
          </p>

          {/* Descriptor */}
          <p
            data-reveal
            className="text-[#1A2B3C]/40 mx-auto leading-relaxed transition-all duration-700 delay-200 opacity-0 translate-y-8"
            style={{ fontSize: 15, maxWidth: 460, marginBottom: 32 }}
          >
            Discover people through intentions, ideas and conversations — not appearances.
          </p>

          {/* Buttons */}
          <div
            data-reveal
            className="flex flex-col sm:flex-row gap-3 justify-center transition-all duration-700 delay-300 opacity-0 translate-y-8"
          >
            <Link to="/signup">
              <Button
                size="sm"
                className="w-full sm:w-auto px-5 text-sm font-medium shadow-sm"
                style={{ height: 42 }}
              >
                Create a free account
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto px-5 text-sm font-medium border-[#1A2B3C]/15 text-[#1A2B3C]/50 hover:text-[#1A2B3C] hover:border-[#1A2B3C]/30"
                style={{ height: 42 }}
              >
                Sign in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 1 — Philosophy / Abstract ═══ */}
      <section className="pb-28 sm:pb-36 px-4 sm:px-6 lg:px-8 bg-[#f4f7fc]" style={{ paddingTop: 56 }}>
        <div className="max-w-6xl mx-auto">
          {/* A different approach — header */}
          <h2
            data-reveal
            className="text-3xl sm:text-4xl font-bold text-center text-[#1A2B3C] mb-3 transition-all duration-700 opacity-0 translate-y-8"
          >
            A different approach
          </h2>
          <p
            data-reveal
            className="text-[#1A2B3C]/50 text-center max-w-xl mx-auto mb-14 transition-all duration-700 delay-100 opacity-0 translate-y-8"
          >
            No more superficial profiles. Tractk focuses on what truly matters.
          </p>

          {/* 3 illustrations — floating, no cards, separated by thin lines */}
          <div
            data-reveal
            className="grid grid-cols-1 md:grid-cols-3 transition-all duration-700 delay-300 opacity-0 translate-y-8"
          >
            {/* Illustration 1 — face line drawing */}
            <div className="flex flex-col items-center justify-start text-center px-4">
              <img
                src="/illustration-1.png"
                alt="Words before looks"
                className="max-w-[420px] h-[260px] w-full object-contain"
              />
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-[#1A2B3C] mb-2">Words before looks</h4>
                <p className="text-[#1A2B3C]/50 text-sm leading-relaxed max-w-[240px]">
                  Conversations start from what matters, not from appearances.
                </p>
              </div>
            </div>

            {/* Illustration 2 — overlapping circles */}
            <div className="flex flex-col items-center justify-start text-center px-4 border-y md:border-y-0 md:border-x border-[#1A2B3C]/10">
              <img
                src="/illustration-2.png"
                alt="Compatibility, not coincidence"
                className="max-w-[480px] h-[260px] w-full object-contain"
              />
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-[#1A2B3C] mb-2">Compatibility, not coincidence</h4>
                <p className="text-[#1A2B3C]/50 text-sm leading-relaxed max-w-[240px]">
                  Our algorithm connects people based on values, interests and personality.
                </p>
              </div>
            </div>

            {/* Illustration 3 — concentric circles */}
            <div className="flex flex-col items-center justify-start text-center px-4">
              <img
                src="/illustration-3.png"
                alt="Connections that last"
                className="max-w-[340px] h-[240px] w-full object-contain"
              />
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-[#1A2B3C] mb-2">Connections that last</h4>
                <p className="text-[#1A2B3C]/50 text-sm leading-relaxed max-w-[240px]">
                  Built on authenticity, respect and real understanding.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 — Human / Real People ═══ */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#f9fafb]">
        <div className="max-w-5xl mx-auto">
          <h2
            data-reveal
            className="text-3xl sm:text-4xl font-bold text-center text-[#1A2B3C] mb-3 transition-all duration-700 opacity-0 translate-y-8"
          >
            Real people, real words
          </h2>
          <p
            data-reveal
            className="text-[#1A2B3C]/50 text-center max-w-xl mx-auto mb-16 transition-all duration-700 delay-100 opacity-0 translate-y-8"
          >
            On Tractk, every profile tells a story. No photos upfront — just personality, passions and intentions.
          </p>

          {/* Profile cards row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Profile 1 */}
            <div
              data-reveal
              className="bg-white rounded-2xl p-8 border border-[#e8ecf2] transition-all duration-700 delay-100 opacity-0 translate-y-8"
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-full bg-[#E8EDF5] flex items-center justify-center">
                  <span className="text-[#6B8FD4] font-semibold text-lg">L</span>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2B3C]">Lena, 28</p>
                  <p className="text-sm text-[#1A2B3C]/40">Paris &middot; Looking for deep conversations</p>
                </div>
              </div>
              <p className="text-[#1A2B3C]/70 text-[15px] leading-relaxed mb-5 italic">
                &ldquo;I believe the best connections start with curiosity, not appearances. I&rsquo;d rather know what keeps you up at night than what you look like at brunch.&rdquo;
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Philosophy</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Jazz</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Creative writing</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Cinema</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#6B8FD4]">
                <span className="w-2 h-2 rounded-full bg-[#6B8FD4]/40" />
                Intention: Meaningful friendship
              </div>
            </div>

            {/* Profile 2 */}
            <div
              data-reveal
              className="bg-white rounded-2xl p-8 border border-[#e8ecf2] transition-all duration-700 delay-200 opacity-0 translate-y-8"
            >
              <div className="flex items-center gap-4 mb-5">
                <div className="w-12 h-12 rounded-full bg-[#E8EDF5] flex items-center justify-center">
                  <span className="text-[#5A7EC2] font-semibold text-lg">M</span>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2B3C]">Marco, 34</p>
                  <p className="text-sm text-[#1A2B3C]/40">Lyon &middot; Open to romance</p>
                </div>
              </div>
              <p className="text-[#1A2B3C]/70 text-[15px] leading-relaxed mb-5 italic">
                &ldquo;I&rsquo;m an architect who thinks in shapes and stories. I want to meet someone who sees the world with the same wonder — someone I can talk to for hours without noticing.&rdquo;
              </p>
              <div className="flex flex-wrap gap-2 mb-5">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Architecture</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Travel</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Poetry</span>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-[#EEF2F9] text-[#5A7EC2]">Cooking</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-[#6B8FD4]">
                <span className="w-2 h-2 rounded-full bg-[#6B8FD4]/40" />
                Intention: Romantic connection
              </div>
            </div>
          </div>

          {/* Conversation snippet */}
          <div
            data-reveal
            className="max-w-lg mx-auto bg-white rounded-2xl p-8 border border-[#e8ecf2] transition-all duration-700 delay-300 opacity-0 translate-y-8"
          >
            <p className="text-xs uppercase tracking-widest text-[#6B8FD4] font-medium mb-6">A conversation on Tractk</p>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8EDF5] flex-shrink-0 flex items-center justify-center">
                  <span className="text-[#6B8FD4] text-xs font-semibold">L</span>
                </div>
                <div className="bg-[#f4f7fc] rounded-2xl rounded-tl-sm px-4 py-3">
                  <p className="text-sm text-[#1A2B3C]/80">I saw you&rsquo;re into Murakami — have you read Kafka on the Shore? It completely changed how I think about loneliness.</p>
                </div>
              </div>
              <div className="flex gap-3 flex-row-reverse">
                <div className="w-8 h-8 rounded-full bg-[#E8EDF5] flex-shrink-0 flex items-center justify-center">
                  <span className="text-[#5A7EC2] text-xs font-semibold">M</span>
                </div>
                <div className="bg-[#6B8FD4]/10 rounded-2xl rounded-tr-sm px-4 py-3">
                  <p className="text-sm text-[#1A2B3C]/80">Yes! That book stayed with me for weeks. There&rsquo;s something about the way he writes silence that feels almost architectural.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E8EDF5] flex-shrink-0 flex items-center justify-center">
                  <span className="text-[#6B8FD4] text-xs font-semibold">L</span>
                </div>
                <div className="bg-[#f4f7fc] rounded-2xl rounded-tl-sm px-4 py-3">
                  <p className="text-sm text-[#1A2B3C]/80">That&rsquo;s a beautiful way to put it. I think we&rsquo;re going to have great conversations.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Modes */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#F0F2F8]">
        <div className="max-w-5xl mx-auto">
          <h2
            data-reveal
            className="text-3xl sm:text-4xl font-bold text-center text-[#1A2B3C] mb-4 transition-all duration-700 opacity-0 translate-y-8"
          >
            3 ways to discover
          </h2>
          <p
            data-reveal
            className="text-neutral-500 text-center max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 opacity-0 translate-y-8"
          >
            Find people based on your criteria, your way.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div
              data-reveal
              className="text-center transition-all duration-700 delay-100 opacity-0 translate-y-8"
            >
              <div className="w-16 h-16 bg-[#E8EDF5] rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPinIcon className="w-8 h-8 text-[#6B8FD4]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A2B3C] mb-2">Near me</h3>
              <p className="text-neutral-500 text-sm">
                Discover profiles close to you. Filter by city, radius and age range.
              </p>
            </div>

            <div
              data-reveal
              className="text-center transition-all duration-700 delay-200 opacity-0 translate-y-8"
            >
              <div className="w-16 h-16 bg-[#E8EDF5] rounded-full flex items-center justify-center mx-auto mb-4">
                <GroupIcon className="w-8 h-8 text-[#5A7EC2]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A2B3C] mb-2">By affinity</h3>
              <p className="text-neutral-500 text-sm">
                The algorithm calculates your thematic compatibility. Art, music, science — find your peers.
              </p>
            </div>

            <div
              data-reveal
              className="text-center transition-all duration-700 delay-300 opacity-0 translate-y-8"
            >
              <div className="w-16 h-16 bg-[#E8EDF5] rounded-full flex items-center justify-center mx-auto mb-4">
                <CompassIcon className="w-8 h-8 text-[#6B8FD4]" />
              </div>
              <h3 className="text-lg font-semibold text-[#1A2B3C] mb-2">By intention</h3>
              <p className="text-neutral-500 text-sm">
                Friendship, romance, projects or discussions — find people who want the same things.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#6B8FD4] to-[#5A7EC2]">
        <div
          data-reveal
          className="max-w-3xl mx-auto text-center transition-all duration-700 opacity-0 translate-y-8"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to make real connections?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Join a community that values authenticity.
          </p>
          <Link to="/signup">
            <Button
              size="lg"
              className="bg-white text-[#5A7EC2] hover:bg-blue-50 focus:ring-white"
            >
              Join Tractk
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-primary-100 bg-white">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-neutral-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#6B8FD4] rounded flex items-center justify-center">
              <span className="text-white font-bold text-xs">T</span>
            </div>
            <span>Tractk v1.0.0</span>
          </div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#5A7EC2]">Terms</a>
            <a href="#" className="hover:text-[#5A7EC2]">Privacy</a>
            <a href="#" className="hover:text-[#5A7EC2]">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
